// Shared design-system component builders — one function per Figma component,
// emitting the canonical .ar-* markup so Figma ⇄ DS build ⇄ app stay 1:1.
// Builders are pure: they return HTML strings and escape their own inputs
// (callers pass raw values), matching the buildReminderPolicyFormHtml pattern.
//
// Behaviour stays caller-side — these emit markup only. The design-system.html
// playground and every screen should render components through here rather than
// hand-typing the markup (which is how parallel .app-* classes drifted in).
import { escapeHtml } from "../lib/format.js";

// Read-only label/value row. Mirrors the Figma "Table / td (Type=Label with
// value)" component (node 11:98): a stacked label + value with an 8px gap.
// Used by the account pages (Profile, Company) for their detail rows.
//
//   buildLabelValueCell({ label, value })                      → plain row
//   buildLabelValueCell({ label, value, small: true })         → 13px value
//   buildLabelValueCell({ label, valueHtml: "<img …>" })       → custom value node
//   buildLabelValueCell({ label, value, valueClass: "…" })     → extra value class
//
// `valueHtml` is emitted raw (caller's responsibility to escape) — it exists
// for the one cell (company logo) whose value is markup, not text.
export function buildLabelValueCell({
  label,
  value = "",
  valueHtml = null,
  small = false,
  valueClass = "",
  valueId = "",
} = {}) {
  let valueNode;
  if (valueHtml != null) {
    valueNode = valueHtml;
  } else {
    const cls = `ar-td-value${small ? " ar-td-value-sm" : ""}${valueClass ? ` ${valueClass}` : ""}`;
    valueNode = `<p class="${cls}"${valueId ? ` id="${escapeHtml(valueId)}"` : ""}>${escapeHtml(value)}</p>`;
  }
  return `<div class="ar-td-cell ar-td-label-value ar-td-label-value-fill">
            <p class="ar-td-label">${escapeHtml(label)}</p>
            ${valueNode}
          </div>`;
}

// ── Field builders ─────────────────────────────────────────────────────────
// These emit markup only. Behaviour stays caller-side: field-a11y.js auto-wires
// aria-invalid/aria-describedby off [data-field-error]; setFieldError toggles
// the error node + [data-field-label] error class; select display-text mirroring
// and auto-grow are wired per screen. The error hooks (data-field-label on the
// label, data-field-error on the error <p>) are load-bearing — do not rename.

// Serialise an attrs map into ` key="value"` pairs (true → boolean attr).
function attrPairs(attrs) {
  let out = "";
  for (const [k, v] of Object.entries(attrs || {})) {
    if (v === undefined || v === null || v === false || v === "") continue;
    out += v === true ? ` ${k}` : ` ${k}="${escapeHtml(String(v))}"`;
  }
  return out;
}

// "full" → `.ar-input-spec-full` (width:100%); the invoice-compose widths
// ("half"/"item"/"qty"/"price") already exist as `.invoice-compose-field-*`.
function fieldWidthClass(prefix, widthModifier) {
  if (!widthModifier) return "";
  if (widthModifier === "full") return ` ${prefix}-full`;
  return ` invoice-compose-field-${widthModifier}`;
}

// Text / email / password / number / date input. Mirrors the Figma input field.
//   error: true  → renders the per-field `data-field-error` slot (most forms)
//   error: false → no slot (drawers that show one shared error message)
//   labelStyle: "caption" → small uppercase label (reminder subject fields)
//   widthModifier: "full" | "half" | "item" | "qty" | "price"
//   attrs: { autocomplete, autofocus, inputmode, pattern, maxlength, min, max,
//            step, "data-item-field", ... } — rare HTML passthroughs
export function buildTextInput({
  label,
  name = "",
  type = "text",
  value = "",
  placeholder = "",
  required = false,
  disabled = false,
  hint = "",
  // A line under the field (.ar-input-spec-note-text): what the value means,
  // where `hint`, over it, says what to type.
  note = "",
  error = true,
  fieldKey = name,
  widthModifier = null,
  labelStyle = "default",
  id = "",
  labelId = "",
  inlineStyle = "",
  attrs = {},
} = {}) {
  const labelCls = `ar-input-spec-label${labelStyle === "caption" ? " ar-input-spec-label-caption" : ""}`;
  const control =
    `<input class="ar-input-spec-control" type="${escapeHtml(type)}"` +
    `${name ? ` name="${escapeHtml(name)}"` : ""}${id ? ` id="${escapeHtml(id)}"` : ""}` +
    ` value="${escapeHtml(value)}"${placeholder ? ` placeholder="${escapeHtml(placeholder)}"` : ""}` +
    `${required ? " required" : ""}${disabled ? " disabled" : ""}` +
    `${inlineStyle ? ` style="${escapeHtml(inlineStyle)}"` : ""}${attrPairs(attrs)} />`;
  return `<label class="ar-input-spec${fieldWidthClass("ar-input-spec", widthModifier)}"${labelId ? ` id="${escapeHtml(labelId)}"` : ""}>
  <span class="${labelCls}" data-field-label="${escapeHtml(fieldKey)}">${escapeHtml(label)}</span>
  ${hint ? `<p class="ar-input-spec-hint">${escapeHtml(hint)}</p>` : ""}
  <div class="ar-input-spec-field-wrap">${control}</div>
  ${note ? `<p class="ar-input-spec-note-text">${escapeHtml(note)}</p>` : ""}
  ${error ? `<p class="ar-input-spec-error-text" data-field-error="${escapeHtml(fieldKey)}" hidden></p>` : ""}
</label>`;
}

// Textarea field. Same contract as buildTextInput; value becomes element content.
export function buildTextarea({
  label,
  name = "",
  value = "",
  placeholder = "",
  required = false,
  hint = "",
  rows = null,
  error = true,
  fieldKey = name,
  widthModifier = null,
  labelStyle = "default",
  id = "",
  controlClass = "",
} = {}) {
  const labelCls = `ar-textarea-spec-label${labelStyle === "caption" ? " ar-textarea-spec-label-caption" : ""}`;
  const control =
    `<textarea class="ar-textarea-spec-control${controlClass ? ` ${escapeHtml(controlClass)}` : ""}"` +
    `${name ? ` name="${escapeHtml(name)}"` : ""}${id ? ` id="${escapeHtml(id)}"` : ""}` +
    `${rows ? ` rows="${rows}"` : ""}${placeholder ? ` placeholder="${escapeHtml(placeholder)}"` : ""}` +
    `${required ? " required" : ""}>${escapeHtml(value)}</textarea>`;
  return `<label class="ar-textarea-spec${fieldWidthClass("ar-textarea-spec", widthModifier)}">
  <span class="${labelCls}" data-field-label="${escapeHtml(fieldKey)}">${escapeHtml(label)}</span>
  ${hint ? `<p class="ar-textarea-spec-hint">${escapeHtml(hint)}</p>` : ""}
  <div class="ar-textarea-spec-field-wrap">${control}</div>
  ${error ? `<p class="ar-textarea-spec-error-text" data-field-error="${escapeHtml(fieldKey)}" hidden></p>` : ""}
</label>`;
}
// Low-level escape hatch for bare controls that can't use the full field
// structure (e.g. the credit-note in-table quantity input). Markup only.
export function buildBareControl({
  type = "text",
  name = "",
  value = "",
  className = "",
  inlineStyle = "",
  attrs = {},
} = {}) {
  return `<input class="ar-input-spec-control${className ? ` ${className}` : ""}" type="${escapeHtml(type)}"` +
    `${name ? ` name="${escapeHtml(name)}"` : ""} value="${escapeHtml(value)}"` +
    `${inlineStyle ? ` style="${escapeHtml(inlineStyle)}"` : ""}${attrPairs(attrs)} />`;
}

// An optional field folded behind a checkbox, the way "Add a discount" and
// "Request a deposit" already are. Reuses their classes so the three read as
// one group rather than three shapes.
//
// The checkbox decides whether the content reaches the DOCUMENT, not whether
// the form remembers it: unticking hides the fields and drops them from the
// preview and the PDF, but whatever was typed stays in the DOM, so ticking it
// again brings the text back. Callers gate their payload and preview on
// isEnabled() — the hiding is presentation, the gating is the meaning.
//
// One helper rather than three copies. The sender block, formatActivityTimestamp
// and the registration disclosure each shipped as per-screen copies and each
// drifted the moment one of them changed.
export function buildOptionalBlockHtml({ id, label, enabled = false, content = "" }) {
  return `
    <div class="invoice-deposit-block">
      <label class="ar-choice invoice-deposit-toggle-row">
        <input type="checkbox" class="ar-checkbox" id="${escapeHtml(id)}-toggle" ${enabled ? "checked" : ""} />
        ${escapeHtml(label)}
      </label>
      <div class="invoice-deposit-fields" id="${escapeHtml(id)}-fields" ${enabled ? "" : "hidden"}>
        ${content}
      </div>
    </div>`;
}

export function wireOptionalBlock(id, onChange = null) {
  const toggle = document.getElementById(`${id}-toggle`);
  const fields = document.getElementById(`${id}-fields`);
  if (!toggle || !fields) return { isEnabled: () => true };
  toggle.addEventListener("change", () => {
    revealPanel(fields, toggle.checked);
    if (typeof onChange === "function") onChange(toggle.checked);
  });
  return { isEnabled: () => toggle.checked };
}


// Show or hide a block of revealed fields, sliding rather than jumping.
//
// `hidden` stays the resting CLOSED state, so every existing
// `.x-fields[hidden] { display: none }` rule and the server-rendered initial
// markup keep working exactly as before — the animation is only what happens
// between the two states. Heights are inline and cleared on the way out, so
// nothing is left pinned and the block grows if its own content later does.
//
// Everything here is per-element: a second toggle mid-flight cancels the first
// rather than racing it.
const revealTimers = new WeakMap();

export function revealPanel(el, open) {
  if (!el) return;

  const pending = revealTimers.get(el);
  if (pending) {
    window.clearTimeout(pending.timer);
    el.removeEventListener("transitionend", pending.onEnd);
    revealTimers.delete(el);
  }

  const clear = () => {
    el.style.height = "";
    el.style.overflow = "";
    el.style.transition = "";
    el.style.marginTop = "";
  };

  // The gap its parent puts above it. A flex/grid gap appears the INSTANT the
  // element stops being `hidden`, before a single pixel of height has been
  // animated — so without this the content below jumps by the gap (12px under
  // a discount or notes block, 4px in the option list), slides, then snaps
  // back on the way out. Cancelling it with a negative margin that eases to 0
  // makes the space the element occupies go from nothing to gap + height in
  // one continuous movement.
  //
  // This is why the More options panel needed none of it: its parent is a
  // plain block with no gap at all.
  const parentGap = Number.parseFloat(
    getComputedStyle(el.parentElement || el).rowGap
  ) || 0;

  // A duration of 0 means prefers-reduced-motion, or a browser that will not
  // animate this — either way there is no transitionend coming, so do the
  // whole thing synchronously rather than wait for an event that never fires.
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  if (reduce) {
    clear();
    el.hidden = !open;
    return;
  }


  const DURATION = 200;
  let done = false;
  const finish = () => {
    // Both the event and the timer can arrive; whichever is first wins.
    if (done) return;
    done = true;
    // Detach HERE, not only at the top of the next call. The next call reaches
    // that cleanup through the map entry, and this deletes the entry — so a
    // listener left attached now is invisible to it and survives. That is what
    // broke the third tap: open, close, open again ran the CLOSE's listener,
    // whose `open` is false, and it hid the panel the moment it had opened.
    el.removeEventListener("transitionend", onEnd);
    window.clearTimeout(timer);
    clear();
    if (!open) el.hidden = true;
    revealTimers.delete(el);
  };
  const onEnd = (event) => {
    if (event.target === el && event.propertyName === "height") finish();
  };
  let timer = null;

  if (open) el.hidden = false;
  const from = open ? 0 : el.scrollHeight;
  const to = open ? el.scrollHeight : 0;

  el.style.overflow = "hidden";
  el.style.height = `${from}px`;
  el.style.marginTop = `${open ? -parentGap : 0}px`;
  // Force the starting values to be the transition's start, not its end.
  void el.offsetHeight;
  el.style.transition = `height ${DURATION}ms ease, margin-top ${DURATION}ms ease`;
  el.style.height = `${to}px`;
  el.style.marginTop = `${open ? 0 : -parentGap}px`;

  el.addEventListener("transitionend", onEnd);
  // transitionend is not guaranteed — a backgrounded tab drops it, and a
  // zero-height block never animates at all. Land the end state regardless.
  timer = window.setTimeout(finish, DURATION + 50);
  revealTimers.set(el, { timer, onEnd });
}

// The compose form's "More options" disclosure.
//
// A button with aria-expanded/aria-controls rather than <details>. The native
// element was the better answer right up until this needed to ANIMATE: a
// closed <details> doesn't render its content at all, so there is nothing for
// a transition to run on, and the pseudo-element that would fix it
// (::details-content) is not in Safari, which is what this is looked at in.
// aria-expanded on a button pointing at a labelled region is the standard
// disclosure pattern and loses nothing but the free open/close.
//
// `open` is computed by the caller from whether anything INSIDE is in use. An
// invoice with a discount, or a VAT rate that is not this company's usual one,
// must not hide that fact behind a closed section.
export function buildMoreOptionsHtml({ open = false, content = "" }) {
  return `
    <div class="invoice-more-options">
      <button
        class="invoice-more-options-summary"
        id="more-options-toggle"
        type="button"
        aria-expanded="${open ? "true" : "false"}"
        aria-controls="more-options-panel"
      >
        <span>More options</span>
        <span class="invoice-more-options-chevron" aria-hidden="true"></span>
      </button>
      <div class="invoice-more-options-panel${open ? " is-open" : ""}" id="more-options-panel">
        <div class="invoice-more-options-body">
          ${content}
        </div>
      </div>
    </div>`;
}

export function wireMoreOptions() {
  const button = document.getElementById("more-options-toggle");
  const panel = document.getElementById("more-options-panel");
  const body = panel?.querySelector(".invoice-more-options-body");
  if (!button || !panel || !body) return null;

  // The height is measured here rather than left to CSS. `height: auto` is not
  // animatable, and the grid 0fr → 1fr trick measured 0px open in this layout —
  // `1fr` had no free space to claim in a container of indefinite height.
  //
  // `is-settled` is the resting state: height back to auto so the section grows
  // with its own content, and overflow released so a date picker's popover
  // isn't clipped by the panel.
  const settled = () => {
    if (!panel.classList.contains("is-open")) return;
    panel.classList.add("is-settled");
    // Drop the pixel height the open was animated to. The CSS then takes over
    // with `height: auto`, so the section grows when something inside it does
    // — ticking "Add a discount" reveals fields, and a panel still pinned to
    // the height it opened at would clip them.
    panel.style.height = "";
  };

  panel.addEventListener("transitionend", (event) => {
    if (event.propertyName === "height") settled();
  });

  // transitionend is not guaranteed to arrive. It never fires at all when the
  // duration is 0 — which is exactly what prefers-reduced-motion does here —
  // and a backgrounded tab can drop it too. Without this the panel would stay
  // pinned at the pixel height it was opened to: it would not grow when a
  // block inside it expands, and a date picker's popover inside it would stay
  // clipped by an overflow that never got released.
  let settleTimer = null;
  const settleSoon = () => {
    window.clearTimeout(settleTimer);
    const duration = Number.parseFloat(getComputedStyle(panel).transitionDuration) || 0;
    settleTimer = window.setTimeout(settled, duration * 1000 + 50);
  };

  if (panel.classList.contains("is-open")) settled();

  button.addEventListener("click", () => {
    const open = button.getAttribute("aria-expanded") === "true";
    button.setAttribute("aria-expanded", open ? "false" : "true");

    if (open) {
      // Closing: pin the current height first, or the transition has no
      // starting value to move from and the panel simply vanishes.
      panel.style.height = `${body.scrollHeight}px`;
      panel.classList.remove("is-settled");
      // Force a reflow so the pinned height is the transition's start.
      void panel.offsetHeight;
      panel.classList.remove("is-open");
      panel.style.height = "0px";
      window.clearTimeout(settleTimer);
      return;
    }

    panel.classList.add("is-open");
    panel.style.height = `${body.scrollHeight}px`;
    settleSoon();
  });

  return { isOpen: () => button.getAttribute("aria-expanded") === "true" };
}
